<template>
    <b-container fluid class="eval-prompt-management">
        <admin-page-header title="AI 판단 기준"
                           description="회사별 AI 평가 피드백에서 공통 판단 기준을 찾고, 새 기준이 기존 기준보다 나쁘지 않은지 자동으로 시험합니다.">
            <!-- 첫 조회 전·실패 시에는 기본값으로 만든 배지가 실제 상태처럼 보이므로 숨긴다. -->
            <template v-if="loaded" #badge>
                <b-badge variant="light" class="stage-badge mr-2">{{ stage.label }}</b-badge>
                <b-badge :variant="automationStatus.variant" class="stage-badge">
                    {{ automationStatus.label }}
                </b-badge>
            </template>
        </admin-page-header>

        <b-card no-body class="rulebook-shell">
            <b-tabs v-model="tabIndex" card lazy>
                <b-tab :title="tabs[0].label">
                    <RulebookOverviewTab :overview="overview"
                                         :loading="loading"
                                         :loaded="loaded"
                                         :error="error"
                                         @retry="loadOverview"
                                         @refresh-overview="loadOverview"
                    />
                </b-tab>
                <b-tab :title="tabs[1].label">
                    <RulebookFeedbackTab :overview="overview"
                                         :loading="loading"
                                         :loaded="loaded"
                                         :error="error"
                                         @retry="loadOverview"
                    />
                </b-tab>
                <b-tab :title="tabs[2].label">
                    <RulebookVersionsTab @refresh-overview="loadOverview" />
                </b-tab>
                <b-tab :title="tabs[3].label">
                    <RulebookCyclesTab />
                </b-tab>
                <b-tab :title="tabs[4].label">
                    <RulebookValidationTab :overview="overview"
                                           :loading="loading"
                                           :loaded="loaded"
                                           :error="error"
                                           @retry="loadOverview"
                    />
                </b-tab>
            </b-tabs>
        </b-card>
    </b-container>
</template>

<script>
import RulebookCyclesTab from './RulebookCyclesTab.vue';
import RulebookFeedbackTab from './RulebookFeedbackTab.vue';
import RulebookOverviewTab from './RulebookOverviewTab.vue';
import RulebookValidationTab from './RulebookValidationTab.vue';
import RulebookVersionsTab from './RulebookVersionsTab.vue';
import {
    RULEBOOK_TABS,
    apiErrorMessage,
    automationStatusMeta,
    normalizeRulebookOverview,
    stageMeta,
} from './rulebook-presentation';

// URL ?tab=<key> ↔ 탭 위치. 모르는 값은 첫 탭, 첫 탭은 URL 에서 생략한다.
function tabIndexOf(query) {
    const tab = query && query.tab;
    const key = Array.isArray(tab) ? tab[0] : tab;
    return Math.max(0, RULEBOOK_TABS.findIndex((t) => t.key === key));
}

export default {
    name: 'EvalPromptManagement',
    components: {
        RulebookCyclesTab,
        RulebookFeedbackTab,
        RulebookOverviewTab,
        RulebookValidationTab,
        RulebookVersionsTab,
    },
    data() {
        return {
            tabIndex: tabIndexOf(this.$route && this.$route.query),
            tabs: RULEBOOK_TABS,
            overview: normalizeRulebookOverview({}),
            loading: false,
            // 한 번이라도 조회에 성공했는지 — 그 전에는 기본값 overview 를 실제 상태처럼 그리지 않는다.
            loaded: false,
            error: '',
            overviewRequestId: 0,
        };
    },
    computed: {
        stage() {
            return stageMeta(this.overview.rulebook.stage);
        },
        automationStatus() {
            return automationStatusMeta(this.overview);
        },
    },
    watch: {
        tabIndex(index) {
            if (tabIndexOf(this.$route.query) === index) return;
            const key = index > 0 ? RULEBOOK_TABS[index].key : '';
            this.$router.push({query: key ? {tab: key} : {}}).catch(() => {});
        },
        // 뒤로가기·사이드바 재클릭으로 같은 화면의 URL 이 바뀌면 탭을 맞춘다.
        '$route.query.tab'() {
            this.tabIndex = tabIndexOf(this.$route.query);
        },
    },
    created() {
        this.loadOverview();
    },
    methods: {
        async loadOverview() {
            // 스위치·복귀 직후 재조회가 먼저 시작된 조회에 덮이지 않도록 마지막 요청만 반영한다.
            this.overviewRequestId += 1;
            const requestId = this.overviewRequestId;
            this.loading = true;
            this.error = '';
            try {
                const {data} = await this.$axios.get('/eval-prompt/rulebook/overview');
                if (this.overviewRequestId !== requestId) return;
                this.overview = normalizeRulebookOverview(data);
                this.loaded = true;
            } catch (err) {
                if (this.overviewRequestId !== requestId) return;
                // 재조회 실패는 직전 성공 데이터를 지우지 않는다.
                const status = err?.response?.status;
                this.error = apiErrorMessage(
                    err,
                    status === 403
                        ? 'AI 판단 기준 관리 권한이 없습니다.'
                        : 'AI 판단 기준 상태를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.',
                );
            } finally {
                if (this.overviewRequestId === requestId) this.loading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.eval-prompt-management {
    color: #2d3b4d;
    max-width: 1600px;
}

.stage-badge {
    border: 1px solid #d7dee6;
    font-size: 0.78rem;
    padding: 0.55rem 0.7rem;
}

.rulebook-shell {
    border-color: #dce3ea;
    box-shadow: 0 4px 16px rgba(31, 45, 61, 0.05);
}

::v-deep .rulebook-shell > .card-header {
    background: #fff;
    border-bottom: 1px solid #e2e7ec;
    padding: 0 1rem;
}

::v-deep .rulebook-shell > .card-header .nav-tabs {
    border: 0;
}

::v-deep .rulebook-shell > .card-header .nav-link {
    border: 0;
    border-bottom: 3px solid transparent;
    color: #667384;
    font-weight: 600;
    padding: 1rem 1.15rem 0.85rem;
}

::v-deep .rulebook-shell > .card-header .nav-link.active {
    border-bottom-color: #2867b2;
    color: #244f80;
}

::v-deep .rulebook-shell > .tab-content > .tab-pane {
    padding: 1.5rem;
}

@media (max-width: 767px) {
    ::v-deep .rulebook-shell > .card-header .nav-link {
        font-size: 0.85rem;
        padding-left: 0.65rem;
        padding-right: 0.65rem;
    }

    ::v-deep .rulebook-shell > .tab-content > .tab-pane {
        padding: 1rem;
    }
}
</style>
